import { Box, styled } from '@mui/material'
import SelectField from 'components/Search/SelectField'
import DatePicker from 'components/ui/DatePicker'
import Field from 'components/ui/Field'
import subDays from 'date-fns/subDays'
import { useEffect } from 'react'

import { labelWidth } from './common'

const CustomBox = styled(Box)(({ theme }) => ({
  paddingLeft: `calc(${labelWidth}px + ${theme.spacing(2)})`,
  marginBottom: theme.spacing(2),
}))

const OPT_TODAY = 'today'
const OPT_YESTERDAY = 'yesterday'
const OPT_7_DAYS = '7days'
const OPT_14_DAYS = '14days'
const OPT_CUSTOM = 'custom'

const UPDATE_DATE_OPTS = [
  { value: '', label: 'すべての期間' },
  { value: OPT_TODAY, label: '今日' },
  { value: OPT_YESTERDAY, label: '昨日' },
  { value: OPT_7_DAYS, label: '過去7日間' },
  { value: OPT_14_DAYS, label: '過去14日間' },
  { value: OPT_CUSTOM, label: 'カスタム' },
]

/** @param {number} n */
const nDaysAgo = (n) => subDays(new Date(), n)

const dayOptionQuery = (key) => {
  const today = new Date()
  const yesterday = nDaysAgo(1)

  switch (key) {
    case OPT_TODAY:
      return {
        dateFrom: today,
        dateTo: today,
      }
    case OPT_YESTERDAY:
      return {
        dateFrom: yesterday,
        dateTo: yesterday,
      }
    case OPT_7_DAYS:
      return {
        dateFrom: nDaysAgo(7),
        dateTo: today,
      }
    case OPT_14_DAYS:
      return {
        dateFrom: nDaysAgo(14),
        dateTo: today,
      }
    default:
      return {
        dateFrom: null,
        dateTo: null,
      }
  }
}

const isSameDay = (d1: Date | null, d2: Date | null) => {
  return (
    d1 === d2 ||
    (d1 !== null &&
      d2 !== null &&
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate())
  )
}

const DATE_OPTIONS = [OPT_TODAY, OPT_YESTERDAY, OPT_7_DAYS, OPT_14_DAYS]
const getDateOpt = (params, isCustom) => {
  if (isCustom) {
    return OPT_CUSTOM
  }

  if (!params.dateFrom && !params.dateTo) {
    return ''
  }

  const matchedOpt = DATE_OPTIONS.find((opt) => {
    const { dateFrom, dateTo } = dayOptionQuery(opt)
    return isSameDay(dateFrom, params.dateFrom) && isSameDay(dateTo, params.dateTo)
  })

  return matchedOpt || OPT_CUSTOM
}

interface Props {
  name: string
  label: string
  dateFrom: Date | null
  dateTo: Date | null
  isCustom: boolean
  onChange: any
}
const DateSelector = (props: Props) => {
  const { name, label, dateFrom, dateTo, isCustom, onChange } = props

  useEffect(() => {
    if (!isCustom && getDateOpt({ dateFrom, dateTo }, false) === OPT_CUSTOM) {
      onChange({ isCustom: true })
    }
  }, [isCustom, dateFrom, dateTo, onChange])

  const updateDateOpt = getDateOpt({ dateFrom, dateTo }, isCustom)

  const handledDateFromChange = (date: Date | null) => {
    onChange({ dateFrom: date })
  }

  const handleDateToChange = (date: Date | null) => {
    onChange({ dateTo: date })
  }

  const handleDateOptChange = (event) => {
    const { value } = event.target
    if (value === OPT_CUSTOM) {
      onChange({ isCustom: true })
    } else {
      const query = dayOptionQuery(value)
      onChange({
        ...query,
        isCustom: false,
      })
    }
  }

  const customDatePickers = (
    <CustomBox>
      <Field inline label="開始日">
        <DatePicker value={dateFrom} onChange={handledDateFromChange} testid="start-date-picker" />
      </Field>
      <Field inline label="終了日">
        <DatePicker value={dateTo} onChange={handleDateToChange} testid="end-date-picker" />
      </Field>
    </CustomBox>
  )

  return (
    <>
      <SelectField
        name={name}
        label={label}
        value={updateDateOpt}
        onChange={handleDateOptChange}
        options={UPDATE_DATE_OPTS}
        testid="update-date"
      />

      {isCustom && customDatePickers}
    </>
  )
}

export default DateSelector
